import { PauseCircleIcon, StopCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import type { TTSJobStatus, CheckpointId } from '@/types/api';

export interface PauseStatusIndicatorProps {
  status: TTSJobStatus;
  pausedAtCheckpoint?: CheckpointId | string | null;
  pausedAt?: string | null;
  stoppedAt?: string | null;
  stopReason?: string | null;
}

// Human-readable checkpoint labels - must match backend checkpoints
const CHECKPOINT_LABELS: Record<string, string> = {
  before_tts_synthesis: 'Before TTS Synthesis (ElevenLabs)',
  before_credits: 'Before Credits Generation',
  before_ocr: 'Before OCR Processing (GPT Vision)',
  // Fallback for any other checkpoint
  unknown: 'Unknown Checkpoint',
};

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
}

function formatTimeSince(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } catch {
    return '';
  }
}

export default function PauseStatusIndicator({
  status,
  pausedAtCheckpoint,
  pausedAt,
  stoppedAt,
  stopReason,
}: PauseStatusIndicatorProps) {
  // Only show for paused or stopped status
  if (status !== 'paused' && status !== 'stopped') {
    return null;
  }

  // Paused State
  if (status === 'paused') {
    const checkpointLabel = pausedAtCheckpoint
      ? CHECKPOINT_LABELS[pausedAtCheckpoint] || pausedAtCheckpoint
      : 'Unknown checkpoint';
    const timeSince = formatTimeSince(pausedAt);

    return (
      <div className="p-4 bg-warning-50 border border-warning-200 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <PauseCircleIcon className="w-6 h-6 text-warning-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-warning-800">
              Job Paused
            </h4>
            <div className="mt-1 text-sm text-warning-700 space-y-1">
              <p>
                <span className="font-medium">Paused at:</span> {checkpointLabel}
              </p>
              <p className="flex items-center gap-1">
                <ClockIcon className="w-4 h-4" />
                <span>
                  {formatDateTime(pausedAt)}
                  {timeSince && ` (${timeSince})`}
                </span>
              </p>
            </div>
            <p className="mt-2 text-xs text-warning-600">
              Click "Resume Processing" to continue the job from this checkpoint.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Stopped State
  if (status === 'stopped') {
    return (
      <div className="p-4 bg-danger-50 border border-danger-200 rounded-xl">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <StopCircleIcon className="w-6 h-6 text-danger-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-danger-800">
              Job Stopped
            </h4>
            <div className="mt-1 text-sm text-danger-700 space-y-1">
              <p className="flex items-center gap-1">
                <ClockIcon className="w-4 h-4" />
                <span>Stopped: {formatDateTime(stoppedAt)}</span>
              </p>
              {stopReason && (
                <p>
                  <span className="font-medium">Reason:</span> {stopReason}
                </p>
              )}
            </div>
            <p className="mt-2 text-xs text-danger-600">
              This job has been permanently cancelled. Any partial outputs can be
              downloaded below.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
